/**
 * shareContent — texto publicitário centralizado para compartilhamentos.
 *
 * Cada tipo (negocio / promocao / evento / noticia) gera um trio
 * { titulo, texto, url } pronto pra alimentar Web Share API ou clipboard.
 *
 * Branding: sempre "Empreende {Cidade}" + emoji característico + CTA.
 */

const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

const capitalize = (s) => (s && typeof s === 'string')
  ? s.charAt(0).toUpperCase() + s.slice(1)
  : '';

function resolveCidade(municipio, fallbackSlug) {
  if (municipio?.nome) return municipio.nome;
  const slug = municipio?.slug || fallbackSlug || '';
  if (!slug) return 'nossa cidade';
  return slug.replace(/-/g, ' ').split(' ').filter(Boolean).map(capitalize).join(' ');
}

function formatarData(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return ''; }
}

function buildNegocio(dados, municipio) {
  const cidade = resolveCidade(municipio, dados?.municipio_slug);
  const slug = dados?.slug || dados?.id || '';
  const url = `${BASE_URL}/negocio/${slug}`;
  return {
    titulo: `${dados?.nome || 'Negócio'} — Empreende ${cidade}`,
    texto:
      `🌟 Descobri um negócio incrível aqui em *${cidade}*!\n\n` +
      `*${dados?.nome || 'Negócio'}* está na plataforma ` +
      `*Empreende ${cidade}* — o marketplace oficial da nossa cidade — ` +
      `e vale muito a pena conhecer! 🏘️\n\n` +
      `👉 ${url}`,
    url,
  };
}

function buildPromocao(dados, municipio) {
  const cidade = resolveCidade(municipio, dados?.municipio_slug);
  const negocioSlug = dados?.negocio_slug || dados?.business_slug || dados?.negocio_id || '';
  const url = `${BASE_URL}/negocio/${negocioSlug}`;
  const titulo = dados?.titulo || dados?.nome || 'Promoção';
  const negocioNome = dados?.negocio_nome || dados?.business_nome || 'um negócio local';
  return {
    titulo: `🔥 ${titulo} — ${negocioNome}`,
    texto:
      `🔥 Olha essa promoção que encontrei no *Empreende ${cidade}*!\n\n` +
      `*${titulo}* — oferecida por *${negocioNome}*, ` +
      `um negócio daqui da nossa cidade! 💜\n\n` +
      `Corre que pode ser por tempo limitado! ⏳\n` +
      `👉 ${url}`,
    url,
  };
}

function buildEvento(dados, municipio) {
  const cidade = resolveCidade(municipio, dados?.municipio_slug);
  const slug = dados?.slug || dados?.id || '';
  const url = `${BASE_URL}/eventos/${slug}`;
  const data = formatarData(dados?.data_inicio || dados?.data);
  const local = dados?.local || cidade;
  return {
    titulo: `🎉 ${dados?.titulo || dados?.nome || 'Evento'} — ${cidade}`,
    texto:
      `🎉 Tem evento acontecendo aqui em *${cidade}*!\n\n` +
      `*${dados?.titulo || dados?.nome || 'Evento'}*\n` +
      (data ? `📅 ${data}\n` : '') +
      `📍 ${local}\n\n` +
      `Vi no *Empreende ${cidade}* — a plataforma oficial da nossa cidade! 🏘️\n` +
      `👉 ${url}`,
    url,
  };
}

function buildNoticia(dados, municipio) {
  const cidade = resolveCidade(municipio, dados?.municipio_slug);
  const slug = dados?.slug || dados?.id || '';
  const url = `${BASE_URL}/noticias/${slug}`;
  const titulo = dados?.titulo || dados?.nome || 'Notícia';
  return {
    titulo,
    texto:
      `📰 ${titulo}\n\n` +
      `Uma notícia importante para quem é de *${cidade}*! ` +
      `Fiquei sabendo pelo *Empreende ${cidade}* — o portal oficial da nossa cidade. 🏘️\n\n` +
      `👉 ${url}`,
    url,
  };
}

/**
 * Função principal — escolhe o builder pelo tipo.
 *
 * @param {'negocio'|'promocao'|'evento'|'noticia'} tipo
 * @param {Object} dados — entidade compartilhada
 * @param {Object} [municipio] — { nome?, slug? }
 * @returns {{ titulo: string, texto: string, url: string }}
 */
export function gerarTextoCompartilhamento(tipo, dados, municipio) {
  const safe = dados || {};
  switch (tipo) {
    case 'promocao': return buildPromocao(safe, municipio);
    case 'evento':   return buildEvento(safe, municipio);
    case 'noticia':  return buildNoticia(safe, municipio);
    case 'negocio':
    default:         return buildNegocio(safe, municipio);
  }
}
